import {Typography, ScrollView, Divider} from '@genestack/ui';
import React from 'react';

import {PaginatedList} from '../../../../../components/pagination/paginated-list';
import {Preloader} from '../../../../../components/preloader/preloader';
import {SidebarResultsHeader} from '../../../common/search-results-header';
import {UsageItemsGroup} from '../../../../../components/usage-items-group';
import {pluralize} from '../../../../../utils/pluralize';

import {EditModeDocumentNodes} from './document-nodes';
import {
    useFindUsagesInfiniteQuery,
    DOC_PAGE_SIZE,
    UsageItem
} from './hooks/use-find-usages-infinite-query';
import {DocumentEditStatusIcon} from '../../doc-status-icon/document-status-icon';
import {NodeSuggestion} from '../../../common/sidebar-search-field/interface';

interface Props {
    searchData: NodeSuggestion;
    getDocumentLink: (documentId: string, nodeId?: string) => string;
}

function getSearchLabel(totalDocuments = 0, totalNodes = 0) {
    if (!totalDocuments) {
        return 'No results found';
    }

    const usagesLabel = `${totalNodes} ${pluralize('usage', totalNodes)}`;
    const documentLabel = `${totalDocuments} ${pluralize('document', totalDocuments)}`;

    return `${usagesLabel} in ${documentLabel}`;
}

export function FindUsagesResult({searchData, getDocumentLink}: Props) {
    const {isLoading, data, fetchNextPage, isFetchingNextPage} =
        useFindUsagesInfiniteQuery(searchData);

    const renderFindUsagesItem = React.useCallback(
        (usage: UsageItem) => {
            const {accession, type, name, readOnly} = usage.document;

            const element = (
                <UsageItemsGroup
                    key={accession}
                    type={type}
                    readOnly={readOnly}
                    label={name}
                    count={usage.nodes.total}
                    append={
                        <DocumentEditStatusIcon
                            showReadOnly
                            showDeleted={false}
                            document={usage.document}
                        />
                    }
                >
                    <EditModeDocumentNodes
                        docAccession={usage.document.accession}
                        nodesData={usage.nodes}
                        getDocumentLink={getDocumentLink}
                    />
                </UsageItemsGroup>
            );

            return [usage.document.accession, element] as [React.Key, React.ReactElement];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data]
    );

    return (
        <Preloader show={isLoading}>
            {data && (
                <>
                    <SidebarResultsHeader>
                        <Typography variant="section">
                            {getSearchLabel(data?.totalDocuments, data?.totalNodes)}
                        </Typography>
                    </SidebarResultsHeader>

                    <ScrollView>
                        <PaginatedList
                            pageSize={DOC_PAGE_SIZE}
                            items={data.items}
                            isLoadingNextPage={isFetchingNextPage}
                            hasNextPage={data.hasNextPage}
                            onLoadNext={fetchNextPage}
                            renderItem={renderFindUsagesItem}
                        />

                        <Divider variant="transparent" gap={4} />
                    </ScrollView>
                </>
            )}
        </Preloader>
    );
}
